import { setUser } from '@sentry/react'

import { platform } from '../device/platform'
import { initGlobalVariables } from './initializers/global-variables'
import initMicrofrontendsDiscovery from './initializers/discovery'
import initMobile from './mobile'
import getMemoryToken from '../session/token'
import { IPreInit } from '../../interfaces/App/IPreInit'
import readModulesFromDevice from '../versions/read-modules'
import initializeSentry from './initializers/initializeSentry'
import { FileSystem } from '../filesystem/index'
import { renderAuraSplash } from '../../../application/components/Splash/native'
import { registerServiceWorker } from './initializers/service-worker'
import { redirectAccounts } from './services/initial-redirect'
import validateEmptyScreen from './services/validateEmptyScreen'
import { getCacheStorage } from '../cache'
import { EventTracker } from '../event-tracker'
import { initTenant } from '../tenant'
import { initDeepLinks } from '../deep-links'
import { runTokenMigration } from '../migration/token-migration'
import { getInitTracker } from './tracking/init-tracker'
import { getUserIdByToken } from '../session/get-user-from-token'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Side-effects inmediatos (se ejecutan al importar el módulo)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const vh = window.innerHeight * 0.01
document.documentElement.style.setProperty('--vh', `${vh}px`)

redirectAccounts()
renderAuraSplash()
validateEmptyScreen(20)

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Singletons de plataforma (importados por otros módulos de la app)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export const filesystem = platform !== 'web' ? new FileSystem() : undefined
export const cacheStorage = getCacheStorage()

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Pre-inicialización asíncrona
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export default async function preInit (): Promise<IPreInit> {
  const tracker = getInitTracker()

  // ── Observabilidad & Analytics ─────────────────────────────────────────────
  initSentryIfEnabled(tracker)
  await tracker.trackStep('tenant_init', async () => await initTenant())
  tracker.trackStepSync('event_tracker_init', () => EventTracker.getInstance().init(process.env.GTM_CONTAINER_ID))

  // ── Filesystem (solo mobile) ───────────────────────────────────────────────
  if (filesystem !== undefined) {
    await tracker.trackStep('filesystem_init', async () => await filesystem.init())
    await tracker.trackStep('session_migration', async () => await runTokenMigration(filesystem))
  }

  // ── Sesión ─────────────────────────────────────────────────────────────────
  const initialToken = await tracker.trackStep('session_restore', async () => await getMemoryToken(), { hasFilesystem: filesystem !== undefined, platform })

  if (initialToken) {
    await setUserFromToken(tracker, initialToken)
  }

  // ── Discovery de microfrontends ────────────────────────────────────────────
  const microfrontendsResponse = await tracker.trackStep('discovery', async () => await initMicrofrontendsDiscovery(initialToken), { hasToken: !!initialToken })

  // ── Inicialización específica por plataforma ───────────────────────────────
  if (platform !== 'web') {
    await tracker.trackStep('mobile_init', async () => await initMobile(microfrontendsResponse), { modulesCount: microfrontendsResponse.microFrontends.length })
    tracker.trackStepSync('deep_links', () => initDeepLinks())
  } else {
    await tracker.trackStep('service_worker', async () => await registerServiceWorker())
  }

  // ── Variables globales ─────────────────────────────────────────────────────
  const memoryModules = await tracker.trackStep('read_modules_device', async () => await readModulesFromDevice())

  tracker.trackStepSync('global_variables', () => {
    initGlobalVariables({ microfronts: microfrontendsResponse.microFrontends, memoryModules })
  })

  // ── Fin ────────────────────────────────────────────────────────────────────
  tracker.endStep('pre_init_complete', {
    modulesDiscovered: microfrontendsResponse.microFrontends.length,
    modulesInDevice: memoryModules.length,
    hasToken: !!initialToken,
    platform
  })
  tracker.reportSuccess()

  return { discoveredMicrofronts: microfrontendsResponse.microFrontends, initialToken, memoryModules }
}

// ─── Helpers internos ────────────────────────────────────────────────────────

function initSentryIfEnabled (tracker: ReturnType<typeof getInitTracker>): void {
  if (process.env.TRACKING_ACTIVATED === 'true') {
    tracker.trackStepSync('sentry_init', () => initializeSentry())
  }
}

async function setUserFromToken (tracker: ReturnType<typeof getInitTracker>, token: string): Promise<void> {
  await tracker.trackStep('set_user_cache', async () => {
    const uid = getUserIdByToken(token)
    cacheStorage.setUserId(uid)
    if (uid) setUser({ id: String(uid) })
  })
}
